import { type ForwardedRef, forwardRef, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { pluck } from 'ramda'
import type { DocumentFile } from '@pbt/pbt-ui-components'

import { AttachmentMenu } from '~/components/dashboard/communications/conversations-chat/AttachmentMenu'
import useAttachmentHistory from '~/components/dashboard/communications/conversations-chat/useAttachmentHistory'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import {
  attachDocumentsToMessage,
  attachExistingDocumentToMessage,
  clearFileToSend,
} from '~/store/actions/conversationMessages'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getAttachmentsToSend } from '~/store/reducers/conversationMessages'
import type { AttachmentHistoryItem, ConversationFile } from '~/types'
import useDialog from '~/utils/useDialog'

import type { BaseTemplateInputHandle } from './BaseTemplateInput/BaseTemplateInput'
import type { CommunicationTemplateInputProps } from './CommunicationTemplateInput'
import WrappedBaseTemplateInput from './WrappedBaseTemplateInput'

import styles from './CommunicationTemplateInputWithAttachments.module.scss'

export interface CommunicationTemplateInputWithAttachmentsProps extends CommunicationTemplateInputProps {
  attachmentMenuPlacement?: 'top' | 'right' | 'bottom' | 'left'
}

type AttachmentDraftFile = ConversationFile & {
  documentId?: string
  originalDocumentId?: string
  sourceDocumentId?: string
}

const getHistoryAttachmentDocumentId = (
  attachment: Pick<AttachmentHistoryItem, 'documentId' | 'id'>,
) => attachment.documentId || attachment.id

const isHistoryAttachmentInDraft = (
  historyAttachment: AttachmentHistoryItem,
  draftAttachment: AttachmentDraftFile,
) => {
  const historyAttachmentDocumentId =
    getHistoryAttachmentDocumentId(historyAttachment)
  const draftAttachmentDocumentIds = [
    draftAttachment.documentId,
    draftAttachment.originalDocumentId,
    draftAttachment.sourceDocumentId,
    draftAttachment.id,
    draftAttachment.fileUrl,
  ]

  if (!historyAttachmentDocumentId) {
    return false
  }

  return draftAttachmentDocumentIds.includes(historyAttachmentDocumentId)
}

const CommunicationTemplateInputWithAttachments = forwardRef(
  function CommunicationTemplateInputWithAttachments(
    {
      hideEmail,
      clientId,
      patientId,
      eventId,
      attachmentMenuPlacement,
      ...rest
    }: CommunicationTemplateInputWithAttachmentsProps,
    ref: ForwardedRef<BaseTemplateInputHandle>,
  ) {
    const dispatch = useDispatch()
    const attachmentsToSend = useSelector(getAttachmentsToSend)
    const isCommunicationsRightRailEnabled = useSelector(
      getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
    )
    const {
      attachments: attachmentHistory,
      isFetching: isFetchingAttachments,
    } = useAttachmentHistory(
      isCommunicationsRightRailEnabled ? patientId : undefined,
    )
    const { t } = useTranslation(['Common'])
    const attachmentButtonRef = useRef<HTMLButtonElement>(null)

    const [openAttachmentDialog] = useDialog(
      DialogNames.CONVERSATION_MULTI_ATTACHMENT_DIALOG,
    )

    const onDeleteFile = (file: DocumentFile['file']) => {
      dispatch(clearFileToSend(file.id as string))
    }

    const openAttachmentDialogWithContext = () => {
      openAttachmentDialog({ clientId, patientId, eventId, ...rest })
    }

    const handleAddAttachment = () => {
      openAttachmentDialogWithContext()
    }

    const selectedAttachmentIds = useMemo(
      () =>
        attachmentHistory
          .map((attachment) => ({
            documentId: getHistoryAttachmentDocumentId(attachment),
            isInDraft: attachmentsToSend.some((file) =>
              isHistoryAttachmentInDraft(
                attachment,
                file as AttachmentDraftFile,
              ),
            ),
          }))
          .filter(
            (
              attachmentInfo,
            ): attachmentInfo is { documentId: string; isInDraft: true } =>
              Boolean(attachmentInfo.documentId) && attachmentInfo.isInDraft,
          )
          .map(({ documentId }) => documentId),
      [attachmentHistory, attachmentsToSend],
    )

    const attachmentHistoryByDocumentId = useMemo(
      () =>
        attachmentHistory.reduce<Record<string, AttachmentHistoryItem>>(
          (acc, attachment) => {
            const documentId = getHistoryAttachmentDocumentId(attachment)
            if (documentId) {
              acc[documentId] = attachment
            }
            return acc
          },
          {},
        ),
      [attachmentHistory],
    )

    const handleToggleAttachment = (documentId: string, nextValue: boolean) => {
      const attachmentFromHistory = attachmentHistoryByDocumentId[documentId]

      if (nextValue) {
        dispatch(
          attachExistingDocumentToMessage(
            attachmentFromHistory || { documentId },
          ),
        )
        return
      }

      const draftAttachmentToRemove = attachmentsToSend.find((file) =>
        isHistoryAttachmentInDraft(
          attachmentFromHistory || {},
          file as AttachmentDraftFile,
        ),
      )

      dispatch(clearFileToSend(draftAttachmentToRemove?.id || documentId))
    }

    const shouldShowAttachmentMenu =
      isCommunicationsRightRailEnabled &&
      Boolean(patientId) &&
      (attachmentHistory.length > 0 || isFetchingAttachments)

    const onAttachClick = () => {
      if (!shouldShowAttachmentMenu) {
        openAttachmentDialogWithContext()
      }
    }

    const onAttachDocuments = (documents: { id: string }[]) => {
      dispatch(
        attachDocumentsToMessage(pluck('id', documents) as string[], {
          clientId,
          patientId,
          appointmentId: eventId,
        }),
      )
    }

    return (
      <>
        <WrappedBaseTemplateInput
          showAttachment
          attachmentButtonRef={attachmentButtonRef}
          classes={{
            title: styles.title,
            titleContainer: styles.titleContainer,
          }}
          clientId={clientId}
          eventId={eventId}
          files={attachmentsToSend}
          filter={['Communication template']}
          hideEmail={hideEmail}
          label={t('Common:COMMUNICATION_TEMPLATE')}
          patientId={patientId}
          ref={ref}
          onAttachClick={onAttachClick}
          onAttachDocuments={onAttachDocuments}
          onDeleteFile={onDeleteFile}
          {...rest}
        />
        {shouldShowAttachmentMenu && (
          <AttachmentMenu
            anchorRef={attachmentButtonRef}
            attachments={attachmentHistory}
            isLoading={isFetchingAttachments}
            placement={attachmentMenuPlacement}
            selectedAttachmentIds={selectedAttachmentIds}
            onAddAttachment={handleAddAttachment}
            onToggleAttachment={handleToggleAttachment}
          />
        )}
      </>
    )
  },
)

export default CommunicationTemplateInputWithAttachments
